/**
 * LoginPage.tsx: Tela de login do Órbita.
 *
 * Produto independente: NÃO copiar do Nectaris OS (ADR-001, UX.md §0).
 * Identidade da marca Órbita (igual à landing /orbita): galáxia sobre navy
 * com acento primário CIANO. Repaginação B2: a tela usa a biblioteca base
 * (src/components/ui) sob o escopo de tokens dark (classe .dark no wrapper)
 * com o acento sobrescrito localmente para o ciano da marca; superfícies,
 * status, radius e foco visível vêm todos dos tokens de src/index.css.
 * Logo oficial: wordmark branco em /orbita/assets/logo-orbita.png.
 * Firebase Auth email/senha, sem auto-registro (admin cria usuários).
 */

import { useState, useEffect, useRef } from "react";
import type { CSSProperties } from "react";
import { useNavigate } from "react-router";
import { signInWithEmailAndPassword } from "firebase/auth";
import { auth } from "@/lib/firebase";
import {
  Button,
  Card,
  EmptyState,
  ErrorState,
  Field,
  InlineError,
  Input,
} from "@/components/ui";
import { useAuth } from "./AuthContext";
import { GalaxyBackground } from "./GalaxyBackground";
import { AvisoCard } from "./AvisoCard";
import { AVISOS } from "./avisos";
import type { Aviso, AvisoCategoria, AvisoSeveridade } from "./avisos";

// ---------------------------------------------------------------------------
// Cor de marca do login: ciano Órbita (o mesmo da landing /orbita). É o único
// hex local da tela: vira o --color-accent DENTRO do escopo do login (override
// no wrapper raiz), então botão, anel de foco, eyebrows, dots e badge "info"
// herdam o ciano via token, sem hex espalhado pelo JSX.
// ---------------------------------------------------------------------------
const ORBITA_CYAN = "#37B7D6"; // cor de marca do login (acento primário)
const ORBITA_CYAN_HOVER = "#7ED4E8"; // hover do CTA, mesma escala da landing

// Override local de tema: aplicado como custom properties no wrapper raiz.
// Mesmo mecanismo do white-label ([data-theme]), só que escopado à tela.
const LOGIN_THEME_VARS = {
  "--color-accent": ORBITA_CYAN,
  "--color-accent-hover": ORBITA_CYAN_HOVER,
  "--color-accent-subtle": `color-mix(in srgb, ${ORBITA_CYAN} 12%, transparent)`,
} as CSSProperties;

// Peso de severidade para ordenação (maior = mais urgente, aparece primeiro).
const SEVERIDADE_PESO: Record<AvisoSeveridade, number> = {
  critico: 3,
  atencao: 2,
  sucesso: 1,
  info: 0,
};

// Ordem de exibição (AVISOS-EDITORIA §3.1): plataforma primeiro (pode impactar
// a operação), depois novidades, depois comercial. A paginação preserva esta
// ordem ao concatenar os grupos antes de fatiar em páginas.
const ORDEM_CATEGORIAS: AvisoCategoria[] = ["plataforma", "novidade", "comercial"];

// Quantos cards por página (slide). Fixo para que TODO slide tenha a mesma
// quantidade de cards, o mesmo grid e a mesma altura (padronização).
const CARDS_POR_PAGINA = 2;

// Ordena um grupo por severidade decrescente e, no empate, prazo mais próximo.
function ordenarAvisos(lista: Aviso[]): Aviso[] {
  return [...lista].sort((a, b) => {
    const peso = SEVERIDADE_PESO[b.severidade] - SEVERIDADE_PESO[a.severidade];
    if (peso !== 0) return peso;
    if (a.prazo && b.prazo) return a.prazo.localeCompare(b.prazo);
    if (a.prazo) return -1;
    if (b.prazo) return 1;
    return 0;
  });
}

// ---------------------------------------------------------------------------
// Logo oficial do Órbita: wordmark branco (raiz do site, fundo navy do login)
// ---------------------------------------------------------------------------
function OrbitaLogo() {
  return (
    <img
      src="/orbita/assets/logo-orbita.png"
      alt="Órbita"
      style={{ height: "40px", width: "auto", display: "block" }}
    />
  );
}

// ---------------------------------------------------------------------------
// Spinner inline
// ---------------------------------------------------------------------------
function InlineSpinner() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 14 14"
      aria-hidden="true"
      style={{ animation: "spin 1s linear infinite" }}
    >
      <circle
        cx="7"
        cy="7"
        r="5.5"
        stroke="currentColor"
        strokeWidth="1.5"
        fill="none"
        strokeDasharray="22"
        strokeDashoffset="11"
      />
    </svg>
  );
}

// ---------------------------------------------------------------------------
// Mensagens de erro do Firebase em português claro
// ---------------------------------------------------------------------------
function translateFirebaseError(code: string): string {
  switch (code) {
    case "auth/user-not-found":
    case "auth/wrong-password":
    case "auth/invalid-credential":
      return "E-mail ou senha inválidos. Verifique e tente novamente.";
    case "auth/user-disabled":
      return "Esta conta foi desativada. Contate o administrador.";
    case "auth/too-many-requests":
      return "Muitas tentativas. Aguarde alguns minutos antes de tentar novamente.";
    case "auth/network-request-failed":
      return "Falha de conexão. Verifique sua internet e tente novamente.";
    case "auth/invalid-email":
      return "Formato de e-mail inválido.";
    default:
      return "Ocorreu um erro ao entrar. Tente novamente.";
  }
}

function getErrorCode(err: unknown): string {
  if (
    err != null &&
    typeof err === "object" &&
    "code" in err
  ) {
    const code = (err as Record<string, unknown>)["code"];
    if (typeof code === "string") return code;
  }
  return "unknown";
}

// ---------------------------------------------------------------------------
// Página do carrossel: uma grade de CARDS_POR_PAGINA cards (mesmo grid, mesma
// altura em toda página). A trilha vive dentro de cada card (AvisoCard).
// ---------------------------------------------------------------------------
function AvisosPagina({ avisos }: { avisos: Aviso[] }) {
  return (
    <div className="avisos-grid">
      {avisos.map((aviso) => (
        <AvisoCard key={aviso.id} aviso={aviso} />
      ))}
    </div>
  );
}

// Fatia uma lista ordenada em páginas de tamanho fixo (padronização de slides).
function paginar<T>(itens: T[], porPagina: number): T[][] {
  const paginas: T[][] = [];
  for (let i = 0; i < itens.length; i += porPagina) {
    paginas.push(itens.slice(i, i + porPagina));
  }
  return paginas;
}

// ---------------------------------------------------------------------------
// Carrossel de páginas: mostra uma PÁGINA de CARDS_POR_PAGINA cards por vez e
// cicla pelas páginas auto-avançando a cada AUTO_MS, com fade suave. Toda
// página tem o mesmo nº de cards, o mesmo grid e a mesma altura (padronização).
// Navegável por dots (uma por página) e pausa no hover/foco. Altura estável:
// todos os slides ocupam a MESMA célula de grade (gridArea 1/1), então o
// contêiner assume a altura da página mais alta e nunca "pula".
// StrictMode-safe: o intervalo é criado num efeito e limpo no cleanup.
// ---------------------------------------------------------------------------
const AUTO_MS = 7500;

function AvisosCarousel({
  paginas,
}: {
  paginas: Aviso[][];
}) {
  const [ativo, setAtivo] = useState(0);
  const [pausado, setPausado] = useState(false);
  // Índice em ref para o timer ler o valor atual sem recriar o efeito.
  const ativoRef = useRef(0);
  ativoRef.current = ativo;

  const total = paginas.length;

  // Auto-avanço. Um único slide não agenda timer. Pausa no hover/foco.
  useEffect(() => {
    if (total <= 1 || pausado) return;
    const id = window.setInterval(() => {
      setAtivo((i) => (i + 1) % total);
    }, AUTO_MS);
    return () => {
      window.clearInterval(id);
    };
  }, [total, pausado]);

  const irPara = (i: number) => {
    setAtivo(((i % total) + total) % total);
  };

  return (
    <div
      role="group"
      aria-roledescription="carrossel"
      aria-label="Novidades e avisos"
      onMouseEnter={() => { setPausado(true); }}
      onMouseLeave={() => { setPausado(false); }}
      onFocusCapture={() => { setPausado(true); }}
      onBlurCapture={() => { setPausado(false); }}
    >
      {/* Palco: todos os slides empilhados na mesma célula (altura estável). */}
      <div style={{ display: "grid" }}>
        {paginas.map((avisos, i) => {
          const ativoSlide = i === ativo;
          return (
            <div
              key={i}
              aria-hidden={!ativoSlide}
              style={{
                gridArea: "1 / 1",
                opacity: ativoSlide ? 1 : 0,
                transform: ativoSlide ? "translateY(0)" : "translateY(6px)",
                transition: "opacity 500ms ease, transform 500ms ease",
                pointerEvents: ativoSlide ? "auto" : "none",
                // Slide inativo não deve ser tabulável/lido nem afetar cliques.
                visibility: ativoSlide ? "visible" : "hidden",
              }}
            >
              <AvisosPagina avisos={avisos} />
            </div>
          );
        })}
      </div>

      {/* Dots: uma por página; clicável para pular; realce da ativa. */}
      {total > 1 && (
        <div
          style={{
            display: "flex",
            gap: "0.5rem",
            marginTop: "1.75rem",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {paginas.map((_, i) => {
            const ativoDot = i === ativo;
            return (
              <button
                key={i}
                type="button"
                className="ui-focusable"
                onClick={() => { irPara(i); }}
                aria-label={`Ir para a página ${String(i + 1)} de ${String(total)}`}
                aria-current={ativoDot ? "true" : undefined}
                style={{
                  height: "6px",
                  width: ativoDot ? "28px" : "12px",
                  padding: 0,
                  border: "none",
                  borderRadius: "var(--radius-pill)",
                  cursor: "pointer",
                  background: ativoDot
                    ? "var(--color-accent)"
                    : "color-mix(in srgb, var(--color-text-muted) 45%, transparent)",
                  transition: "width 300ms ease, background-color 300ms ease",
                }}
              />
            );
          })}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Componente principal
// ---------------------------------------------------------------------------
export function LoginPage() {
  const navigate = useNavigate();
  const { state, sendPasswordReset } = useAuth();

  // --- Login state ---
  const [email, setEmail]       = useState("");
  const [password, setPassword] = useState("");
  const [error, setError]       = useState<string | null>(null);
  const [loading, setLoading]   = useState(false);

  // --- Reset state (todos os hooks antes de qualquer return condicional) ---
  const [resetMode, setResetMode]       = useState(false);
  const [resetEmail, setResetEmail]     = useState("");
  const [resetSent, setResetSent]       = useState(false);
  const [resetError, setResetError]     = useState<string | null>(null);
  const [resetLoading, setResetLoading] = useState(false);

  // Redireciona se já autenticado (após todos os hooks)
  if (state.status === "authenticated") {
    void navigate("/app", { replace: true });
    return null;
  }

  const handleSubmit = (e: React.SyntheticEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    signInWithEmailAndPassword(auth, email, password)
      .then(() => {
        void navigate("/app", { replace: true });
      })
      .catch((err: unknown) => {
        setError(translateFirebaseError(getErrorCode(err)));
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleReset = (e: React.SyntheticEvent<HTMLFormElement>) => {
    e.preventDefault();
    setResetError(null);
    setResetLoading(true);

    sendPasswordReset(resetEmail)
      .then(() => {
        setResetSent(true);
      })
      .catch((err: unknown) => {
        setResetError(translateFirebaseError(getErrorCode(err)));
      })
      .finally(() => {
        setResetLoading(false);
      });
  };

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------
  return (
    /*
     * Layout dividido (split) sobre a galáxia espiral animada.
     * Fundo: GalaxyBackground (canvas absoluto sobre --ink) atrás de tudo.
     * Conteúdo em wrapper relative (z-index acima do canvas), em duas metades:
     *   METADE A: formulário de login/reset (Card translúcido, cantos ciano).
     *   METADE B: painel comercial/avisos (carrossel de AvisoCard).
     * Desktop: duas colunas (md:grid-cols-2). Mobile: empilha (form em cima).
     * A classe .dark ativa os tokens semânticos escuros SÓ nesta tela; o
     * LOGIN_THEME_VARS troca o acento para o ciano da marca (ver topo).
     */
    <div
      className="dark min-h-screen relative overflow-hidden"
      style={{ background: "var(--ink)", ...LOGIN_THEME_VARS }}
    >
      {/* Fundo: galáxia animada (atrás de tudo) */}
      <GalaxyBackground />

      {/* Grade técnica sutil sobre a galáxia (NAVEGACAO.md §6) */}
      <div
        aria-hidden="true"
        style={{
          position: "absolute",
          inset: 0,
          backgroundImage: `
            linear-gradient(color-mix(in srgb, var(--bone) 3%, transparent) 1px, transparent 1px),
            linear-gradient(90deg, color-mix(in srgb, var(--bone) 3%, transparent) 1px, transparent 1px)
          `,
          backgroundSize: "80px 80px, 80px 80px",
          pointerEvents: "none",
        }}
      />

      {/* Wrapper de conteúdo, acima do canvas */}
      <div
        className="relative grid min-h-screen grid-cols-1 md:grid-cols-2"
        style={{ zIndex: 1 }}
      >
        {/* ============================================================= */}
        {/* METADE A: FORMULÁRIO                                          */}
        {/* ============================================================= */}
        <div className="flex items-center justify-center px-4 py-12 md:min-h-screen">
          {/* Painel central: Card da base, translúcido sobre a galáxia */}
          <Card
            role="main"
            padding="2.5rem"
            style={{
              position: "relative",
              width: "100%",
              maxWidth: "400px",
              margin: "0 1rem",
              background: "color-mix(in srgb, var(--color-bg-surface) 92%, transparent)",
              backdropFilter: "blur(6px)",
              WebkitBackdropFilter: "blur(6px)",
              boxShadow:
                "0 0 0 1px color-mix(in srgb, var(--color-accent) 15%, transparent), 0 24px 48px color-mix(in srgb, var(--ink) 40%, transparent)",
            }}
          >
            {/* Cantos decorativos no acento ciano (marca Órbita) */}
            <span
              aria-hidden="true"
              style={{
                position: "absolute",
                top: "-1px",
                left: "-1px",
                width: "16px",
                height: "16px",
                borderTop: "2px solid var(--color-accent)",
                borderLeft: "2px solid var(--color-accent)",
                borderRadius: "var(--radius-card) 0 0 0",
              }}
            />
            <span
              aria-hidden="true"
              style={{
                position: "absolute",
                bottom: "-1px",
                right: "-1px",
                width: "16px",
                height: "16px",
                borderBottom: "2px solid var(--color-accent)",
                borderRight: "2px solid var(--color-accent)",
                borderRadius: "0 0 var(--radius-card) 0",
              }}
            />

            {/* Header: logo + wordmark */}
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: "0.75rem",
                marginBottom: "2rem",
              }}
            >
              <OrbitaLogo />
              <p
                style={{
                  fontFamily: '"IBM Plex Mono", monospace',
                  fontSize: "0.625rem",
                  color: "var(--color-accent)",
                  margin: "0.25rem 0 0",
                  letterSpacing: "0.08em",
                  textTransform: "uppercase",
                }}
              >
                by Nectaris
              </p>
            </div>

            {/* Formulário: modo login ou recuperação */}
            {!resetMode ? (
              <form onSubmit={handleSubmit} noValidate>
                {/* Campo e-mail (Field+Input da base: foco visível via accent) */}
                <Field
                  label="E-mail corporativo"
                  htmlFor="login-email"
                  style={{ marginBottom: "1rem" }}
                >
                  <Input
                    id="login-email"
                    type="email"
                    autoComplete="email"
                    required
                    value={email}
                    onChange={(e) => { setEmail(e.target.value); }}
                    disabled={loading}
                    placeholder="voce@empresa.com"
                    style={{ background: "var(--color-bg-base)" }}
                  />
                </Field>

                {/* Campo senha */}
                <Field
                  label="Senha"
                  htmlFor="login-password"
                  style={{ marginBottom: "1.5rem" }}
                >
                  <Input
                    id="login-password"
                    type="password"
                    autoComplete="current-password"
                    required
                    value={password}
                    onChange={(e) => { setPassword(e.target.value); }}
                    disabled={loading}
                    placeholder="••••••••"
                    style={{ background: "var(--color-bg-base)" }}
                  />
                </Field>

                {/* Erro inline (ErrorState da base: discreto, sem derrubar) */}
                {error && (
                  <div style={{ marginBottom: "1rem" }}>
                    <ErrorState message={error} />
                  </div>
                )}

                {/* Botão entrar (Button da base: acento ciano do escopo) */}
                <Button
                  type="submit"
                  disabled={loading || !email || !password}
                  style={{ width: "100%" }}
                >
                  {loading ? (
                    <>
                      <InlineSpinner />
                      Entrando…
                    </>
                  ) : (
                    "Entrar"
                  )}
                </Button>

                {/* Link recuperar senha */}
                <div style={{ textAlign: "center", marginTop: "1.25rem" }}>
                  <button
                    type="button"
                    className="ui-focusable"
                    onClick={() => { setResetMode(true); setError(null); }}
                    style={{
                      background: "none",
                      border: "none",
                      borderRadius: "var(--radius)",
                      fontFamily: '"Inter", sans-serif',
                      fontSize: "0.8125rem",
                      color: "var(--color-text-muted)",
                      cursor: "pointer",
                      textDecoration: "underline",
                      textDecorationColor:
                        "color-mix(in srgb, var(--color-text-muted) 40%, transparent)",
                      padding: 0,
                    }}
                  >
                    Esqueci a senha
                  </button>
                </div>
              </form>
            ) : (
              /* Modo recuperação de senha */
              <form onSubmit={handleReset} noValidate>
                <p
                  style={{
                    fontFamily: '"Inter", sans-serif',
                    fontSize: "0.875rem",
                    color: "var(--color-text-secondary)",
                    marginBottom: "1.25rem",
                    textAlign: "center",
                  }}
                >
                  Informe seu e-mail para receber o link de redefinição de senha.
                </p>

                {!resetSent ? (
                  <>
                    <Field
                      label="E-mail corporativo"
                      htmlFor="reset-email"
                      style={{ marginBottom: "1.25rem" }}
                    >
                      <Input
                        id="reset-email"
                        type="email"
                        autoComplete="email"
                        required
                        value={resetEmail}
                        onChange={(e) => { setResetEmail(e.target.value); }}
                        disabled={resetLoading}
                        placeholder="voce@empresa.com"
                        style={{ background: "var(--color-bg-base)" }}
                      />
                    </Field>

                    {resetError && (
                      <div style={{ marginBottom: "1rem" }}>
                        <InlineError message={resetError} />
                      </div>
                    )}

                    <Button
                      type="submit"
                      disabled={resetLoading || !resetEmail}
                      style={{ width: "100%" }}
                    >
                      {resetLoading ? "Enviando…" : "Enviar link"}
                    </Button>
                  </>
                ) : (
                  <p
                    style={{
                      fontFamily: '"Inter", sans-serif',
                      fontSize: "0.875rem",
                      color: "var(--color-status-green)",
                      textAlign: "center",
                      padding: "0.75rem",
                      background: "var(--color-status-green-soft)",
                      borderRadius: "var(--radius)",
                      border: "1px solid var(--color-status-green)",
                    }}
                  >
                    Link enviado para <strong>{resetEmail}</strong>. Verifique sua caixa de entrada.
                  </p>
                )}

                <div style={{ textAlign: "center", marginTop: "1.25rem" }}>
                  <button
                    type="button"
                    className="ui-focusable"
                    onClick={() => {
                      setResetMode(false);
                      setResetSent(false);
                      setResetEmail("");
                      setResetError(null);
                    }}
                    style={{
                      background: "none",
                      border: "none",
                      borderRadius: "var(--radius)",
                      fontFamily: '"Inter", sans-serif',
                      fontSize: "0.8125rem",
                      color: "var(--color-text-muted)",
                      cursor: "pointer",
                      textDecoration: "underline",
                      padding: 0,
                    }}
                  >
                    Voltar ao login
                  </button>
                </div>
              </form>
            )}
          </Card>
        </div>
        {/* Fim METADE A */}

        {/* ============================================================= */}
        {/* METADE B: PAINEL COMERCIAL / AVISOS                           */}
        {/* Conteúdo real: avisos tipados (avisos.ts) agrupados na ordem  */}
        {/* plataforma > novidade > comercial, com cards sinalizados por  */}
        {/* severidade (Badge por tom semântico) via AvisoCard.           */}
        {/* ============================================================= */}
        <aside
          aria-label="Novidades e avisos"
          className="flex items-start justify-center px-6 py-12 md:min-h-screen md:items-center"
          style={{ minWidth: 0 }}
        >
          <div
            style={{
              width: "100%",
              maxWidth: "760px",
            }}
          >
            <h2
              style={{
                fontFamily: '"Space Grotesk", sans-serif',
                fontSize: "1.5rem",
                fontWeight: 500,
                color: "var(--color-text-primary)",
                margin: "0 0 0.5rem",
              }}
            >
              Novidades &amp; Avisos
            </h2>
            <p
              style={{
                fontFamily: '"IBM Plex Mono", monospace',
                fontSize: "0.625rem",
                color: "var(--color-accent)",
                letterSpacing: "0.08em",
                textTransform: "uppercase",
                margin: "0 0 1.75rem",
              }}
            >
              Órbita · plataforma de inteligência operacional
            </p>

            {/*
             * Conteúdo real: avisos vindos do array tipado `AVISOS`
             * (src/features/auth/avisos.ts), agrupados por categoria na ordem
             * plataforma > novidade > comercial e ordenados por severidade.
             *
             * Estados: hoje o dado é estático, então só existe conteúdo ou o
             * estado vazio honesto ("Tudo em dia"). Quando os avisos passarem a
             * vir de fonte remota, tratar loading (skeleton) e error (linha
             * discreta) aqui; o login nunca depende deste painel (secundário).
             */}
            {AVISOS.length === 0 ? (
              /* Estado vazio honesto (AVISOS-EDITORIA §3.3), via base */
              <EmptyState
                title="Tudo em dia"
                description="Nenhum aviso no momento. As novidades e comunicados da plataforma aparecem aqui."
              />
            ) : (
              /*
               * Carrossel de PÁGINAS: os avisos são concatenados na ordem
               * plataforma > novidade > comercial (cada grupo já ordenado por
               * severidade/prazo) e fatiados em páginas de CARDS_POR_PAGINA
               * cards. Todo slide tem o mesmo nº de cards, o mesmo grid e a
               * mesma altura (padronização). A trilha (seção) de cada aviso
               * vira um eyebrow no acento dentro do próprio card (AvisoCard).
               * Auto-avança a cada AUTO_MS, dots por página, pausa no
               * hover/foco, altura estável. Conteúdo dos cards intacto.
               * Cards em grade 1col (mobile)/2col (>=900px) via .avisos-grid.
               */
              <AvisosCarousel
                paginas={paginar(
                  ORDEM_CATEGORIAS.flatMap((cat) =>
                    ordenarAvisos(AVISOS.filter((a) => a.categoria === cat)),
                  ),
                  CARDS_POR_PAGINA,
                )}
              />
            )}
          </div>
        </aside>
        {/* Fim METADE B */}
      </div>

      {/* Rodapé */}
      <p
        style={{
          position: "absolute",
          bottom: "1.5rem",
          left: 0,
          right: 0,
          textAlign: "center",
          fontFamily: '"IBM Plex Mono", monospace',
          fontSize: "0.625rem",
          color: "var(--color-text-muted)",
          letterSpacing: "0.04em",
          zIndex: 2,
          pointerEvents: "none",
        }}
      >
        © 2026 Nectaris · Órbita
      </p>

      {/* Keyframe para spinner + grade responsiva dos avisos */}
      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        .avisos-grid {
          display: grid;
          grid-template-columns: 1fr;
          gap: 1rem;
          /* Cards alinhados ao topo e com altura livre: cada card cresce com o
             seu conteúdo (resumo inteiro, sem corte), respeitando o minHeight
             comum para consistência visual entre cards curtos. */
          align-items: start;
        }
        @media (min-width: 900px) {
          .avisos-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
        }
      `}</style>
    </div>
  );
}
